import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { trackCTAClick } from "@/lib/analytics";

interface SeasonalBannerProps {
  id: string; // Unique ID for this banner campaign (e.g., "winter-2024", "spring-sale-2024")
  message: string;
  ctaText?: string;
  ctaLink?: string;
  variant?: "promotion" | "announcement" | "tip";
}

const SeasonalBanner = ({ 
  id, 
  message, 
  ctaText, 
  ctaLink,
  variant = "promotion" 
}: SeasonalBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const storageKey = `seasonal-banner-dismissed-${id}`;

  useEffect(() => {
    const dismissed = localStorage.getItem(storageKey);
    if (!dismissed) {
      setIsVisible(true);
    }
  }, [storageKey]);

  const handleDismiss = () => {
    localStorage.setItem(storageKey, "true");
    setIsVisible(false);
  };

  const handleCTAClick = () => {
    if (ctaLink) {
      trackCTAClick(ctaText || "Learn More", "seasonal_banner", ctaLink);
    }
  };

  if (!isVisible) return null;

  const variantStyles = {
    promotion: "bg-gradient-to-r from-primary via-accent to-primary animate-gradient",
    announcement: "bg-gradient-to-r from-secondary/90 to-secondary-foreground/90",
    tip: "bg-gradient-to-r from-primary/80 to-accent/80",
  };

  return (
    <div className={`${variantStyles[variant]} text-white py-3 px-4 relative overflow-hidden`}>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
      
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 relative z-10">
        <div className="flex items-center gap-3 flex-1">
          <Sparkles className="h-5 w-5 flex-shrink-0 animate-pulse" />
          <p className="text-sm md:text-base font-medium">
            {message}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {ctaText && ctaLink && (
            <Button
              variant="secondary"
              size="sm"
              asChild
              className="hidden sm:flex"
              onClick={handleCTAClick}
            >
              <Link to={ctaLink}>{ctaText}</Link>
            </Button>
          )}
          
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeasonalBanner;
